import { DOCUMENT, NgClass } from "@angular/common";
import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { FooterComponent } from "../shared/footer/footer.component";
import { Router, RouterLink } from "@angular/router";
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import { NgbAccordionModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SignalsStoreService } from '../shared/signals-store.service';
import { ModalContentData } from '../shared/modal-content/modal-content';
import { ModalContentService } from '../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../shared/constants/modal-content-types';
import { ResolutionService } from '../shared/resolution.service';
import { CanonicalService } from '../shared/canonical.service';

@Component({
  selector: 'app-home',
  imports: [NgClass, FooterComponent, RouterLink, NgbAccordionModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  host: { 'class': 'home-page' },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeComponent implements OnInit, OnDestroy {

  private meta = inject(Meta);
  private title = inject(Title);
  private document = inject(DOCUMENT);
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);
  private canonicalService = inject(CanonicalService);

  private signalsStoreService = inject(SignalsStoreService);
  private router = inject(Router)
  private activeModal = inject(NgbModal);
  private modalContentService = inject(ModalContentService);
  private resolutionService = inject(ResolutionService);

  #localBusinessScript?: HTMLScriptElement;
  #faqScript?: HTMLScriptElement;

  isMobile = this.resolutionService.isMobile;

  hasSession = computed(() => this.signalsStoreService.hasSession())

  cardDifferent = [
    {
      title: 'Easy ordering and delivery',
      img: 'assets/images/landing/home/different/1.webp',
      url: ''
    },
    {
      title: 'The best tasting food',
      img: 'assets/images/landing/home/different/2.webp',
      url: ''
    },
    {
      title: 'Over 1,000+ local products',
      img: 'assets/images/landing/home/different/3.webp',
      url: ''
    },
    {
      title: 'Sustainably grown, ethically raised',
      img: 'assets/images/landing/home/different/4.webp',
      url: ''
    },
    {
      title: 'Sustainable delivery, low waste',
      img: 'assets/images/landing/home/different/5.webp',
      url: ''
    },
    {
      title: 'Healthy food for all',
      img: 'assets/images/landing/home/different/6.webp',
      url: ''
    },
  ]

  testimonials = [
    {
      img: 'assets/images/landing/testimonials/katie.jpg',
      quote: 'We try to get everything from Philly Foodworks. I mean, amazing, wonderful food arrives at my house, what else could I ask for? To know that our dollar is going to local food systems and we are able to support things we care about makes me feel a little lighter. It\'s so nice to put money towards what you want to see in the world.',
      name: 'Katie H.',
      meta: 'Customer 7 Years',
      isAlternate: false,
    },
    {
      img: 'assets/images/landing/testimonials/solomon-d.jpg',
      quote: 'I think we\'ve been using Philly FoodWorks for a year now and it has completely transformed our lives. The produce is noticeably tastier and better consistency. The meat is so much more delicious and you feel better about buying the meat given the sustainable and humane practices that the farmers use. Couldn\'t be happier.',
      name: 'Solomon D.',
      meta: 'Customer 1 Year',
      isAlternate: true,
    },
    {
      img: 'assets/images/landing/testimonials/stevenb1.webp',
      quote: 'The best food purchasing decision I have ever made! I feel better, I eat more fruits and veggies than ever before. We order the produce box, local mushroom subscription, poultry, and meat boxes too! It has been life-changing and liberating for me while being cost-efficient and environmentally friendly. Save farms and buy from PFW!',
      name: 'Steven B.',
      meta: 'Customer 7 Years',
      isAlternate: false,
    }
  ]

  producers = [
    {
      title: 'Birchrun Hills Farm',
      img: 'assets/images/landing/producer-card/1.jpg',
      text: 'Birchrun Hills Farm is a small family dairy farm with an on-site creamery in northern Chester County, Pennsylvania.',
      link: 'https://www.phillyfoodworks.com/pages/producers/birchrun-hills-farm-dkcq'
    },
    {
      title: 'Honey Brook Harvest Collective',
      img: 'assets/images/landing/producer-card/2.jpg',
      text: 'Located in Honey Brook, PA the Harvest Collective is a group of farmers that all grow and contribute to our food system we\'re working hard to develop.',
      link: 'https://www.phillyfoodworks.com/pages/producers/honey-brook-harvest-collective-pnbz'
    },
    {
      title: 'Gobblers Ridge Farm',
      img: 'assets/images/landing/producer-card/3.jpg',
      text: 'Gobbler\'s Ridge Farm is a farm nestled in Lancaster County where the wild turkeys gobble in the morning and the deer graze in the night.',
      link: 'https://www.phillyfoodworks.com/pages/producers/gobblers-ridge-farm-sbhk'
    }
  ]


  faq = [
    {
      count: 1,
      question: 'Do I have to have a subscription?',
      answer: 'No, a subscription is not required. We designed our system to be flexible so you can order in the way that works best for you. During sign-up, you can choose to add produce boxes or subscribe and save items, but you do not have to. You can also create an account with no subscriptions and place orders week by week.'
    },
    {
      count: 2,
      question: 'How does ordering work?',
      answer: 'When you shop the Philly Foodworks market, you gain access to the freshest local produce in our region. Each week, farmers share their availability, and we list those items on our website. Your shopping window opens on Friday morning and closes at 10 am the day before your delivery. During that time, you can edit any subscriptions in your account or add items from our online market.<br><br>At 10 am the day before delivery, the market closes for you, and our team begins gathering food from producers and assembling your order. On delivery day, we pack everything and load it into our refrigerated vans. You will receive text updates when your driver leaves the warehouse and is on the way to you.<br><br>Your credit card is charged on the day of delivery. Half of every dollar you spend goes directly back to our local producers to help them continue growing and supplying fresh food for our community.'
    },
    {
      count: 3,
      question: 'What percentage of my dollar goes back to the farmers?',
      answer: 'Fifty percent of every dollar you spend goes directly to our producers. In the conventional food system, farmers receive only about 14 cents. Of the remaining dollar, another 40 cents stays in our local economy. We are a local company run by people just like you, and we’re proud to keep your dollars close to home.'
    },
    {
      count: 4,
      question: 'Why does local food seem more expensive?',
      answer: 'Our farmers use growing practices that respect both people and the planet. When you pay fair wages and use regenerative farming methods, the true cost of food becomes more visible. While that can make local food seem more expensive, it avoids the hidden costs of climate damage and exploitation—so in many ways, it actually costs less.<br><br>Local food is also fresher and free from cost-cutting additives, making it healthier for you. Your body will thank you for investing in real food.<br><br>To make local food more affordable, we offer discounts on select items when you subscribe or buy in bulk. These are listed in our “Market Savings” category. We also run vendor promotions and regular sales to help more people enjoy local food.'
    },
    {
      count: 5,
      question: 'What does your company do to manage food waste?',
      answer: 'It starts with careful crop planning during the winter. We work with our farmers to plan what we will buy from them so they can grow exactly what is needed. This approach helps prevent overproduction and differs from the conventional system, where farmers are often encouraged to overgrow so middlemen can drive prices down.<br><br>Only about 3 to 5 percent of what we purchase is not sold through our market. This includes items we choose not to send for quality reasons or products we overpurchase. Anything still in good condition is donated to a network of eight community groups. Items that are not fit for consumption are composted at Winding Lane Farm, where we source many of our baby greens, cucumbers, tomatoes, carrots, and scallions. We even backhaul compost to Winding Lane when our vans pick up produce for the following week. Yep—your food becomes your food!'
    },
    {
      count: 6,
      question: 'How do I know the food is local?',
      answer: 'We define local as anything grown or made within 150 miles of Philadelphia. Each product lists the farmer or food artisan who produced it, so you always know where it came from. About 90 percent of everything we sell is local.<br><br>We do carry a few non-local staples to round out your cooking needs and to supplement what our farmers can provide during the winter. Even when items are not local, we do our best to source them directly from sustainable farmers.'
    },
    {
      count: 7,
      question: 'Do you only sell local food?',
      answer: 'Not entirely. We do bring in some non-local items to supplement what our region can produce. We know that people still want bananas, citrus, and other staples that do not grow locally.<br><br>We work with Farmart, a local, family-owned produce distributor that helps us source these items. During the summer, only 1 to 5 percent of our produce is non-local. In the winter, that number increases to about 20 to 30 percent. We also source directly from trusted farms along the East Coast that share the same farming values and standards as our local partners.<br><br>We offer these non-local options to give customers more variety and encourage long-term support for local food. In fact, through our partnership with Farmart, we have been able to sell products from our local farms into restaurants across the city—helping more local growers thrive.'
    },
    {
      count: 8,
      question: 'Is your food organic or chemical-free?',
      answer: 'We prioritize chemical-free, regenerative agriculture as much as possible, and much of our produce is certified organic and certified IPM (Integrated Pest Management). Many of our smaller partner farms cannot afford certification, but they use organic methods all the same. For these farmers, sustainability is personal, and they avoid anything that could harm their families working alongside them in the fields.'
    },
    {
      count: 9,
      question: 'How are you reducing packaging waste?',
      answer: 'Your order is packed using returnable, compostable, and recyclable materials. Returnable items include the box, insulated cold sleeve, ice pack, and plastic clamshells—these make up about 80 percent of our packaging.<br><br>Most produce is either packed loose, in returnable clamshells, or in compostable materials that can be composted at home or through a local composting service.'
    },
    {
      count: 10,
      question: 'How do you ensure the quality of the food being delivered?',
      answer: 'With more than 10 years of experience packing produce and local food, we take great care to protect your items and maintain freshness. Our staff receives thorough training, and every order is packed carefully—from heaviest to lightest—to prevent damage.<br><br>All deliveries are made in our branded refrigerated vans to keep food at the proper temperature. You will receive delivery updates so you know when to expect your order, and you’ll often see the same friendly driver each week. Our drivers are Philly Foodworks employees, which helps ensure consistent care and product quality from start to finish.'
    }
  ];


  activeTab = 0;

  tabs = [
    {
      step: "STEP 1",
      label: "We Find What's Fresh",
      title: "We Find What's Fresh",
      desc: "Each week, we check in with our farmers, growers and producers, then update our market and farm shares with the latest, freshest offerings.",
      img: "assets/images/landing/home/how-it-works-tab/corn.webp"
    },
    {
      step: "STEP 2",
      label: "Fill Your Basket",
      title: "You Start Filling Your Basket",
      desc: "Each Friday, the market opens with everything new for the week. Customize your basket with produce, pantry, meats, dairy and more. You can edit your order until 10 a.m. the day before delivery.",
      img: "assets/images/landing/home/how-it-works-tab/2.webp"
    },
    {
      step: "STEP 3",
      label: "We Pick and Pack",
      title: "We Pick and Pack",
      desc: "Once your order's locked in, our growers get busy picking, gathering, and harvesting. Then we bring your food from their farms to our Hunting Park warehouse, where we pack your box.",
      img: "assets/images/landing/home/how-it-works-tab/3.webp"
    },
    {
      step: "STEP 4",
      label: "Fresh Delivery",
      title: "Fresh Delivery",
      desc: "Once your box is packed, it's on the road to your door. And because it was harvested within 72 hours of arriving, your produce shows up crisp, fresh, and full of flavor.",
      img: "assets/images/landing/home/how-it-works-tab/4.webp"
    }
  ];


  private observer?: IntersectionObserver;

  constructor() {
    afterNextRender(() => {
      this.initScrollAnimations();
    });
  }

  ngOnInit() {
    this.setUpSEOTags();
  }

  private initScrollAnimations() {
    const sections: HTMLElement[] = Array.from(
      this.elementRef.nativeElement.querySelectorAll('.has-animation')
    );

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.renderer.addClass(entry.target, 'animated');
        } else {
          this.renderer.removeClass(entry.target, 'animated');
        }
      });
    }, { threshold: 0.1 });

    sections.forEach(section => this.observer!.observe(section));

    this.destroyRef.onDestroy(() => {
      this.observer?.disconnect();
    });
  }

  private setUpSEOTags() {
    const { mainTitle, mainDescription, baseKeywords: keywords, baseURL } = environment.config.seo;

    this.title.setTitle(mainTitle);
    this.canonicalService.setPageDescription(mainDescription);
    this.canonicalService.setPageImage(`${baseURL}assets/images/landing/home/bg-hero-2.webp`);
    this.canonicalService.setPageType('website');

    this.meta.updateTag({ name: 'keywords', content: keywords.join(', ') });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });

    this.meta.updateTag({ property: 'og:title', content: mainTitle });
    this.meta.updateTag({ property: 'og:url', content: baseURL });
    this.meta.updateTag({ property: 'og:site_name', content: environment.application.name });
    this.meta.updateTag({ property: 'og:image:width', content: '1200' });
    this.meta.updateTag({ property: 'og:image:height', content: '630' });
    this.meta.updateTag({ property: 'og:locale', content: 'en_US' });

    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: mainTitle });
    this.meta.updateTag({ name: 'twitter:url', content: baseURL });

    this.#setLocalBusinessJsonLd();
    this.#setFaqJsonLd();
  }

  #setLocalBusinessJsonLd() {
    if (this.#localBusinessScript) {
      this.renderer.removeChild(this.document.head, this.#localBusinessScript);
      this.#localBusinessScript = undefined;
    }

    const ssrScripts = this.document.head.querySelectorAll('script[type="application/ld+json"]');
    ssrScripts.forEach(s => {
      const text = s.textContent || '';
      if (text.includes('"LocalBusiness"') || text.includes('"FoodEstablishment"') || text.includes('"GroceryStore"')) {
        this.renderer.removeChild(this.document.head, s);
      }
    });

    const lb = environment.config.seo.localBusiness;
    const data: any = {
      '@context': 'https://schema.org',
      '@type': lb.type,
      name: environment.application.name,
      url: environment.config.seo.baseURL,
      description: environment.config.seo.mainDescription,
      telephone: lb.telephone,
      image: lb.image,
      priceRange: lb.priceRange,
      address: { '@type': 'PostalAddress', ...lb.address },
      contactPoint: {
        '@type': 'ContactPoint',
        email: environment.config.contactEmail,
        contactType: 'customer service'
      }
    };
    if (lb.sameAs?.length) data.sameAs = lb.sameAs;
    if (lb.areaServed?.length) data.areaServed = lb.areaServed.map(a => ({ '@type': 'City', name: a.name }));
    if (lb.paymentAccepted) data.paymentAccepted = lb.paymentAccepted;
    if (lb.currenciesAccepted) data.currenciesAccepted = lb.currenciesAccepted;

    const script = this.renderer.createElement('script') as HTMLScriptElement;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    this.renderer.appendChild(this.document.head, script);
    this.#localBusinessScript = script;
  }

  #setFaqJsonLd() {
    if (this.#faqScript) {
      this.renderer.removeChild(this.document.head, this.#faqScript);
      this.#faqScript = undefined;
    }

    const ssrScripts = this.document.head.querySelectorAll('script[type="application/ld+json"]');
    ssrScripts.forEach(s => {
      if ((s.textContent || '').includes('"FAQPage"')) {
        this.renderer.removeChild(this.document.head, s);
      }
    });

    const data = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faq.map(f => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer.replace(/<[^>]+>/g, '') }
      }))
    };

    const script = this.renderer.createElement('script') as HTMLScriptElement;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    this.renderer.appendChild(this.document.head, script);
    this.#faqScript = script;
  }

  ngOnDestroy(): void {
    if (this.#localBusinessScript) {
      this.renderer.removeChild(this.document.head, this.#localBusinessScript);
    }
    if (this.#faqScript) {
      this.renderer.removeChild(this.document.head, this.#faqScript);
    }
  }

  openModalCheckAddress() {
    let data: ModalContentData = {
      checkAddress: {
        fromHome: true,
      }
    };

    if (this.activeModal.hasOpenModals()) this.activeModal.dismissAll();

    this.modalContentService.openModal(ModalContentTypes.CHECK_ADDRESS, data, undefined, 'lg').closed.subscribe((result) => {

      if (result?.validationComplete) {
        if (result?.inDeliveryZone) {
          this.modalContentService.openModal(ModalContentTypes.IN_DELIVERY_ZONE).closed.subscribe((result) => {
            if (result?.confirm) {
              this.router.navigate(['/signup']);
            } else {
              this.router.navigate(['/shop']);
            }
          });
        } else {
          this.modalContentService.openModal(
            ModalContentTypes.PROSPECT,
          ).closed.subscribe((result) => {
            if (!result) {
              this.openModalCheckAddress();
            }
          });
        }
      }
    });
  }
}
